import { useMemo } from "react";
import closeicon from "../../../assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import BackdropModal from "../../common/modal/backdropModal";
import { useTranslation } from "react-i18next";
import { formatPremiumValue } from "../../../utils";

const PersonalCyberBenefitsModal = ({ show, onClose, plans }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();

  // Get current locale for number formatting
  const currentLocale = i18n.resolvedLanguage === "ar" ? "en-US" : "en-US";

  // Raw benefit data with numeric values for dynamic formatting
  const personalCyberBenefitsDataRaw = [
    {
      label: "Identity Theft Protection",
      label_ar: "حماية من سرقة الهوية",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Cyber Extortion Coverage",
      label_ar: "تغطية الابتزاز الإلكتروني",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Data Recovery Services",
      label_ar: "خدمات استرداد البيانات",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Credit Monitoring",
      label_ar: "مراقبة الائتمان",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Fraudulent Transaction Coverage",
      label_ar: "تغطية المعاملات الاحتيالية",
      type: "currency_bhd",
      numericValues: [189, 378, 756, 1323, 1890],
    },
    {
      label: "Cyber Bullying Support",
      label_ar: "دعم التنمر الإلكتروني",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Legal Consultation",
      label_ar: "استشارة قانونية",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Device Security Assessment",
      label_ar: "تقييم أمان الجهاز",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Social Media Monitoring",
      label_ar: "مراقبة وسائل التواصل الاجتماعي",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Maximum Coverage Amount (USD)",
      label_ar: "الحد الأقصى للتغطية (دولار أمريكي)",
      type: "currency_usd",
      numericValues: [500, 1000, 2000, 3500, 5000],
    },
    {
      label: "24/7 Support Hotline",
      label_ar: "خط الدعم على مدار 24/7",
      values: ["✅", "✅", "✅", "✅", "✅"],
    },
    {
      label: "Deductible Amount (BD)",
      label_ar: "مبلغ الخصم (د.ب)",
      type: "currency_bhd",
      numericValues: [5, 5, 5, 5, 5],
    },
  ];

  // Format benefit data dynamically based on locale
  const personalCyberBenefitsData = useMemo(() => {
    return personalCyberBenefitsDataRaw.map((benefit) => {
      // If benefit has numeric values, format them
      if (benefit.numericValues) {
        let values;

        if (benefit.type === "currency_bhd") {
          // Format BHD with 3 decimals
          values = benefit.numericValues.map((value) => {
            const formatted = formatPremiumValue(value, currentLocale, 3);
            return currentLocale === "en-US"
              ? `${formatted} د.ب`
              : `BHD ${formatted}`;
          });
        } else if (benefit.type === "currency_usd") {
          // Format USD without decimals
          values = benefit.numericValues.map((value) => {
            const formatted = new Intl.NumberFormat(currentLocale, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
            return currentLocale === "en-US"
              ? `${formatted} دولار أمريكي`
              : `$${formatted}`;
          });
        }

        return {
          ...benefit,
          values,
        };
      }

      // Return as-is for non-numeric benefits
      return benefit;
    });
  }, [currentLocale]);

  if (!show) return null;

  return (
    <BackdropModal
      show={show}
      onClose={onClose}
      hideClosebtn
      contentClassName="!w-[913px] !max-w-[913px]"
    >
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-6 -mt-6">
          <h2 className="text-[#1A2024] text-xl font-medium">
            {translate(
              "Full list of benefits & covers",
              "القائمة الكاملة للفوائد والتغطيات",
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>

        {/* Content with scrollable area */}
        <div className="w-full max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-6">
            {/* Identity Theft Box */}
            <div className="p-6 px-4 rounded-[10px] bg-[#F6F8F9]">
              <h3 className="text-[#1A2024] text-lg font-medium mb-3">
                {translate("Identity Theft:", "سرقة الهوية:")}
              </h3>
              <p className="text-black text-sm font-normal leading-relaxed mb-3">
                {translate(
                  "Means the unauthorized and/or illegal use of your personal information by a third party without your consent for the purpose of obtaining goods, money or services. Identity theft expenses mean reasonable and necessary expenses (including legal costs) incurred by you, with our prior written consent:",
                  "تعني الاستخدام غير المصرح به و/أو غير القانوني لمعلوماتك الشخصية من قبل طرف ثالث دون موافقتك بغرض الحصول على سلع أو أموال أو خدمات. تعني نفقات سرقة الهوية النفقات المعقولة والضرورية (بما في ذلك التكاليف القانونية) التي تتكبدها، بموافقتنا الخطية المسبقة:",
                )}
              </p>
              <ol className="list-decimal list-inside space-y-2 mb-3 text-black text-sm font-normal">
                <li>
                  {translate(
                    "To correct or reinstate official public records after an identity theft has occurred.",
                    "لتصحيح أو إعادة السجلات العامة الرسمية بعد حدوث سرقة الهوية.",
                  )}
                </li>
                <li>
                  {translate(
                    "For challenging the accuracy or completeness of a consumer credit report.",
                    "للطعن في دقة أو اكتمال تقرير ائتمان المستهلك.",
                  )}
                </li>
                <li>
                  {translate(
                    "For the preparation and attendance at any audit or hearing conducted by a governmental agency",
                    "للتحضير والحضور في أي تدقيق أو جلسة استماع تجريها وكالة حكومية",
                  )}
                </li>
                <li>
                  {translate(
                    "For the dismissal or withdrawal of civil or criminal proceedings on the basis that the alleged liability rests with the perpetrator of the identity theft and not you.",
                    "لرفض أو سحب الإجراءات المدنية أو الجنائية على أساس أن المسؤولية المزعومة تقع على عاتق مرتكب سرقة الهوية وليس أنت.",
                  )}
                </li>
                <li>
                  {translate(
                    "For the removal of criminal or civil judgements wrongly entered against you.",
                    "لإزالة الأحكام الجنائية أو المدنية الصادرة خطأً ضدك.",
                  )}
                </li>
              </ol>
              <p className="text-black text-sm font-normal leading-relaxed">
                {translate(
                  "Actual loss of income that would have been earned, whether for partial or whole day's work, for time reasonable and necessarily taken off your work and away from your work premises, solely to attend to the matters referred to above.",
                  "الخسارة الفعلية في الدخل الذي كان يمكن أن يتم كسبه، سواء لجزء من يوم العمل أو ليوم كامل، للوقت المعقول والضروري الذي تم أخذه من عملك وبعيداً عن مقر عملك، فقط لحضور الأمور المشار إليها أعلاه.",
                )}
              </p>
            </div>

            {/* Online Retail Fraud Box */}
            <div className="p-6 px-4 rounded-[10px] bg-[#F6F8F9]">
              <h3 className="text-[#1A2024] text-lg font-medium mb-3">
                {translate(
                  "Online Retail Fraud:",
                  "الاحتيال في التجزئة عبر الإنترنت:",
                )}
              </h3>
              <p className="text-black text-sm font-normal leading-relaxed">
                {translate(
                  "Reimbursement of your financial loss arising directly from an online marketplace fraud and, consequently, non-delivery of products purchased online.",
                  "تعويض عن خسارتك المالية الناتجة مباشرة عن الاحتيال في السوق عبر الإنترنت، وبالتالي عدم تسليم المنتجات المشتراة عبر الإنترنت.",
                )}
              </p>
            </div>

            {/* Electronic Fund Transfer Fraud Box */}
            <div className="p-6 px-4 rounded-[10px] bg-[#F6F8F9]">
              <h3 className="text-[#1A2024] text-lg font-medium mb-3">
                {translate(
                  "Electronic Fund Transfer Fraud:",
                  "الاحتيال في تحويل الأموال الإلكترونية:",
                )}
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-black text-sm font-normal">
                <li>
                  {translate(
                    "Reimbursement of the financial loss due to unauthorized use of online payment services.",
                    "تعويض عن الخسارة المالية بسبب الاستخدام غير المصرح به لخدمات الدفع عبر الإنترنت.",
                  )}
                </li>
                <li>
                  {translate(
                    "Covers the cost related to blocking and reissuing your payment card.",
                    "يغطي التكلفة المتعلقة بحظر وإعادة إصدار بطاقة الدفع الخاصة بك.",
                  )}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </>
    </BackdropModal>
  );
};

export default PersonalCyberBenefitsModal;
